import { useEffect, useState } from 'react';
import {
  Indicator, ActionIcon, Popover, Stack, Text, Badge, Group, Anchor, ScrollArea,
} from '@mantine/core';
import type { NotificationItem } from '@swiftpay/shared';
import { ORDER_STATUS_LABEL } from '@swiftpay/shared';
import { notificationsApi } from '../api/notifications.js';
import { BellIcon } from '../components/client/clientIcons.js';

const KIND_LABEL: Record<string, string> = {
  pending_price_review: 'Согласование цен',
  price_rejected: 'Цены отклонены',
  terms_rejected: 'КУ отклонены',
  terms_superseded: 'КУ устарели',
  terms_applied: 'КУ применены',
  pending_terms: 'Новые КУ',
  order_unfinalized: 'Возврат в черновик',
  draft_stale_after_apply: 'Пересчёт черновика',
  new_order_for_operator: 'Новый заказ',
  order_status_changed: 'Статус изменён',
  new_client_order: 'Заказ от клиента',
  client_order_changed: 'Заказ изменён',
  pending_counterparty: 'Новый контрагент',
  order_cancelled_by_client: 'Заказ отменён',
  order_returned_to_client: 'Возврат заказа',
  order_withdrawn_by_client: 'Заказ отозван',
  order_comment_updated: 'Комментарий',
  test: 'Тест',
};

function formatPayload(n: NotificationItem): { text: string; href?: string } {
  const p = n.payload as Record<string, unknown>;
  const tmc = (p['tmcNumber'] as string | undefined) ?? '';
  const orderHref: string | undefined = p['orderId'] ? `/orders/${p['orderId']}` : undefined;
  const withHref = (text: string, href: string | undefined) => href ? { text, href } : { text };
  switch (n.kind) {
    case 'pending_price_review':
      return withHref(`Заказ ${tmc} ждёт согласования цен`, orderHref);
    case 'price_rejected':
      return withHref(`Заказ ${tmc}: ${p['note'] ?? 'цены отклонены'}`, orderHref);
    case 'order_unfinalized':
      return withHref(`Заказ ${tmc || ''} возвращён в черновик`.trim(), orderHref);
    case 'new_order_for_operator':
      return withHref(`Новый заказ от менеджера: ${tmc}`, orderHref);
    case 'new_client_order':
      return withHref(`Новый заказ от клиента: ${tmc}`, orderHref);
    case 'client_order_changed':
      return withHref(`Ваш заказ ${tmc} изменён менеджером`, orderHref);
    case 'order_status_changed': {
      const status = (p['status'] as keyof typeof ORDER_STATUS_LABEL | undefined);
      const label = status ? (ORDER_STATUS_LABEL[status] ?? status) : '';
      return withHref(`Заказ ${tmc}: ${label}`, orderHref);
    }
    case 'draft_stale_after_apply':
      return withHref(tmc ? `Черновик ${tmc} пересчитан под новые КУ` : 'Черновик пересчитан под новые КУ', orderHref);
    case 'pending_terms':
      return { text: `Новая версия КУ${p['fileName'] ? `: ${p['fileName']}` : ''}`, href: '/admin/commercial-terms' };
    case 'terms_rejected':
      return { text: 'Ваша загрузка КУ не принята', href: '/admin/commercial-terms' };
    case 'terms_superseded':
      return { text: 'Применена другая версия КУ', href: '/admin/commercial-terms' };
    case 'terms_applied':
      return { text: 'Новая версия КУ применена', href: '/admin/commercial-terms' };
    case 'pending_counterparty':
      return { text: 'Новый контрагент ждёт проверки', href: '/admin/pending-counterparties' };
    case 'order_cancelled_by_client':
      return withHref(`Заказ ${tmc} отменён клиентом`, orderHref);
    case 'order_returned_to_client': {
      const reason = (p['reason'] as string | undefined) ?? '';
      return withHref(
        `Заказ ${tmc} возвращён менеджером на доработку${reason ? `: ${reason}` : ''}`,
        orderHref,
      );
    }
    case 'order_withdrawn_by_client':
      return withHref(`Заказ ${tmc} отозван клиентом на доработку`, orderHref);
    case 'order_comment_updated':
      return withHref(`Клиент обновил комментарий к заказу ${tmc}`, orderHref);
    case 'test':
      return { text: (p['msg'] as string | undefined) ?? 'Тестовое уведомление' };
    default:
      // Неизвестный/будущий kind: показать хоть что-то осмысленное, не пустоту.
      return withHref(tmc ? `Заказ ${tmc}` : 'Событие в системе', orderHref);
  }
}

export function NotificationsBell() {
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [unseen, setUnseen] = useState(0);
  const [open, setOpen] = useState(false);

  const reload = () => notificationsApi.list().then((r) => {
    setItems(r.items);
    setUnseen(r.unseenCount);
  }).catch(() => {});

  useEffect(() => {
    void reload();
    const t = setInterval(() => void reload(), 30000);
    return () => clearInterval(t);
  }, []);

  const onOpen = async (o: boolean) => {
    setOpen(o);
    if (o && unseen > 0) {
      try { await notificationsApi.markAllSeen(); } catch { /* ignore */ }
      setUnseen(0);
    }
  };

  return (
    <Popover opened={open} onChange={onOpen} width={400} position="bottom-end" withArrow>
      <Popover.Target>
        <Indicator label={unseen > 0 ? unseen : undefined} disabled={unseen === 0} color="red">
          {/* Иконка, а не эмодзи: эмодзи рендерится шрифтом ОС и выбивается из стиля. */}
          <ActionIcon
            variant="subtle"
            color="gray"
            size={38}
            radius={10}
            onClick={() => onOpen(!open)}
            aria-label={unseen > 0 ? `Уведомления, непрочитанных: ${unseen}` : 'Уведомления'}
          >
            <BellIcon size={20} />
          </ActionIcon>
        </Indicator>
      </Popover.Target>
      <Popover.Dropdown p={0}>
        {/* Список ограничен по высоте: сервер отдаёт до 50 записей, и без скролла
            выпадашка растягивалась на весь экран (фидбек Artem 07.08.2026).
            Показываем ~5 последних, остальное — прокруткой. */}
        <ScrollArea.Autosize mah={360} type="hover" scrollbarSize={6}>
          <Stack gap="xs" p="sm">
            {items.length === 0 && <Text c="dimmed">Уведомлений нет</Text>}
            {items.map((n) => {
            const { text, href } = formatPayload(n);
            const kindLabel = KIND_LABEL[n.kind] ?? 'Уведомление';
            return (
              <Group key={n.id} justify="space-between" wrap="nowrap" align="flex-start">
                <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                  <Group gap={6} wrap="nowrap">
                    <Badge size="xs" variant="light">{kindLabel}</Badge>
                    {!n.seenAt && <Badge color="red" size="xs">new</Badge>}
                  </Group>
                  <Text size="sm">
                    {href ? <Anchor href={href} onClick={() => setOpen(false)}>{text}</Anchor> : text}
                  </Text>
                  <Text size="xs" c="dimmed">{new Date(n.createdAt).toLocaleString('ru-RU')}</Text>
                </Stack>
              </Group>
            );
            })}
          </Stack>
        </ScrollArea.Autosize>
      </Popover.Dropdown>
    </Popover>
  );
}
